import type { RoofRouteConfig } from '../../config/level.config';
import type { ObstacleDefinition } from '../../obstacles/ObstacleDefinition';
import type { CollectibleSpawnPoint, ObstacleSpawnPoint } from '../segments/SegmentDefinition';
import { COLLECTIBLE_FLOAT_HEIGHT } from '../segments/segmentBuilders';
import type { LayoutRules } from '../terrain/LayoutRules';
import { idealClimb, obstacleSpan, type RoofClimb } from '../terrain/roofRunUp';

/** Line name of the roof bottles (CollectibleSpawnPoint.line). */
export const ROOF_LINE = 'ROOF';

const EPSILON = 1e-6;

/**
 * Bottle line onto the walkable top of an obstacle (container roof, user 2026-10-02): at the usual float
 * height above the roof, from just behind the front edge to just before the rear edge – and, with
 * `config.arc`, first along the ideal double jump up from the run-up (roofRunUp.idealClimb at `speed`, the
 * speed the module is met at), where its take-off lies inside the module: the bottles show the path a runner
 * really takes there, like the roof line of SEG_CONTAINERS_40M. The arc stays inside the obstacle's free
 * run-up, so no bottle hangs in another obstacle.
 *
 * The module has no sockets up there: the points are the line's own (ids `<obstacle socket>_ROOF_<n>`,
 * segment-local like every spawn point), evenly spaced and centred on their stretch.
 */
export function roofLinePoints(
  point: ObstacleSpawnPoint,
  definition: ObstacleDefinition,
  climb: RoofClimb,
  rules: LayoutRules,
  config: RoofRouteConfig,
  speed: number,
): CollectibleSpawnPoint[] {
  const span = obstacleSpan(definition);
  const { x, y: ground, z } = point.position;
  const face = z + span.front;
  const last = z + span.back - config.edgeMargin;
  const ideal = idealClimb(climb, rules, speed);
  const takeOff = face - ideal.takeOff;
  const arc = config.arc && takeOff >= 0;
  const first = arc ? takeOff : face + config.edgeMargin;
  if (last < first - EPSILON) return [];
  const count = Math.floor((last - first) / config.bottleSpacing + EPSILON) + 1;
  const start = first + (last - first - (count - 1) * config.bottleSpacing) / 2;
  return Array.from({ length: count }, (_, i) => {
    const at = start + i * config.bottleSpacing;
    const flight = arc ? ideal.heightAt((at - takeOff) / speed) : 0;
    const feet = at < face ? flight : Math.max(climb.rise, flight);
    return {
      id: `${point.id}_ROOF_${String(i + 1).padStart(2, '0')}`,
      lane: point.lane,
      position: { x, y: ground + feet + COLLECTIBLE_FLOAT_HEIGHT, z: at },
      line: ROOF_LINE,
    };
  });
}
